/**
 * MATLAB Evaluator
 * Executes MATLAB AST nodes and returns values
 */
import { MatlabRuntimeError } from "./types";
import { matlabBuiltins, matlabConstants } from "./builtins";
import { getToolbox } from "./toolboxes";
import { MatlabParser } from "./parser";
import { MatlabLexer } from "./lexer";
import { hasClass, constructObject, getProperty } from "./classdef";
/**
 * MATLAB treats logicals as their own class, which is what makes `a(mask)`
 * select elements while `a([1 2])` selects positions. Quanta stores both as
 * 0/1 numbers, so a comparison result is tagged on the array object instead.
 * The tag is a non-enumerable symbol, so it never shows up in output, in
 * JSON, or in a numeric loop over the array.
 */
const LOGICAL_TAG = Symbol.for("quanta.matlab.logical");
export function markLogical(value) {
    if (Array.isArray(value)) {
        Object.defineProperty(value, LOGICAL_TAG, {
            value: true,
            enumerable: false,
            configurable: true,
        });
    }
    return value;
}
export function isLogicalMask(value) {
    return (Array.isArray(value) &&
        value[LOGICAL_TAG] === true);
}
// Operators whose result is a logical array rather than a numeric one.
const PREDICATE_OPERATORS = new Set([
    "==",
    "~=",
    "<",
    "<=",
    ">",
    ">=",
    "&",
    "|",
    "&&",
    "||",
]);
/**
 * Check if a value is an async operation marker
 */
export function isAsyncOperation(value) {
    return (typeof value === "object" &&
        value !== null &&
        "type_" in value &&
        value.type_ === "asyncOp");
}
// Control flow signals
class ReturnSignal {
    values;
    constructor(values) {
        this.values = values;
    }
}
class BreakSignal {
}
class ContinueSignal {
}
/**
 * MATLAB execution environment
 */
export class MatlabEnvironment {
    variables = new Map();
    parent;
    outputSuppressed = false;
    ioContext = null;
    constructor(parent = null) {
        this.parent = parent;
        // Initialize with constants if root environment
        if (!parent) {
            for (const [name, value] of Object.entries(matlabConstants)) {
                this.variables.set(name, value);
            }
        }
    }
    get(name) {
        if (this.variables.has(name)) {
            return this.variables.get(name);
        }
        if (this.parent) {
            return this.parent.get(name);
        }
        return undefined;
    }
    set(name, value) {
        this.variables.set(name, value);
    }
    has(name) {
        if (this.variables.has(name))
            return true;
        if (this.parent)
            return this.parent.has(name);
        return false;
    }
    setOutputSuppressed(suppressed) {
        this.outputSuppressed = suppressed;
    }
    isOutputSuppressed() {
        return this.outputSuppressed;
    }
    setIOContext(context) {
        this.ioContext = context;
    }
    getIOContext() {
        if (this.ioContext)
            return this.ioContext;
        if (this.parent)
            return this.parent.getIOContext();
        return null;
    }
    createChild() {
        const child = new MatlabEnvironment(this);
        // Child inherits IO context from parent
        return child;
    }
    // Get all variable names (for 'who' and 'whos' commands)
    getVariableNames() {
        const names = new Set();
        for (const key of this.variables.keys()) {
            names.add(key);
        }
        return Array.from(names).filter((name) => !Object.keys(matlabConstants).includes(name));
    }
    // Get all variables (for 'save' command)
    getAllVariables() {
        const vars = {};
        for (const [key, value] of this.variables.entries()) {
            // Skip constants
            if (!Object.keys(matlabConstants).includes(key)) {
                vars[key] = value;
            }
        }
        return vars;
    }
    // Clear variables
    clearVariables(names) {
        if (names && names.length > 0) {
            for (const name of names) {
                this.variables.delete(name);
            }
        }
        else {
            // Clear all except constants and declared functions. A script that
            // starts with `clear` still has its local functions afterwards; in
            // MATLAB those are not workspace variables at all.
            const constantNames = Object.keys(matlabConstants);
            for (const [key, value] of Array.from(this.variables.entries())) {
                if (constantNames.includes(key))
                    continue;
                if (typeof value === "object" &&
                    value !== null &&
                    "type" in value &&
                    value.type === "function") {
                    continue;
                }
                this.variables.delete(key);
            }
        }
    }
}
/**
 * MATLAB Evaluator
 */
export class MatlabEvaluator {
    env;
    lastResult = null;
    outputs = [];
    constructor(env) {
        this.env = env || new MatlabEnvironment();
    }
    evaluate(node) {
        try {
            return this.eval(node);
        }
        catch (e) {
            if (e instanceof ReturnSignal) {
                return e.values.length === 1 ? e.values[0] : e.values;
            }
            throw e;
        }
    }
    getEnvironment() {
        return this.env;
    }
    getOutputs() {
        return this.outputs;
    }
    eval(node) {
        switch (node.type) {
            case "NumberLiteral":
                if (node.isImaginary) {
                    return { re: 0, im: node.value };
                }
                return node.value;
            case "StringLiteral":
                return node.value;
            case "Identifier":
                return this.evalIdentifier(node);
            case "BinaryOp":
                return this.evalBinaryOp(node);
            case "UnaryOp":
                return this.evalUnaryOp(node);
            case "FunctionCall":
                return this.evalFunctionCall(node);
            case "ArrayLiteral":
                return this.evalArrayLiteral(node);
            case "CellArray":
                return this.evalCellArray(node);
            case "IndexAccess":
                return this.evalIndexAccess(node);
            case "RangeExpr":
                return this.evalRangeExpr(node);
            case "Assignment":
                return this.evalAssignment(node);
            case "MultiAssignment":
                return this.evalMultiAssignment(node);
            case "FunctionDeclaration":
                return this.evalFunctionDeclaration(node);
            case "AnonymousFunction":
                return this.evalAnonymousFunction(node);
            case "IfStatement":
                return this.evalIfStatement(node);
            case "ForLoop":
                return this.evalForLoop(node);
            case "WhileLoop":
                return this.evalWhileLoop(node);
            case "SwitchStatement":
                return this.evalSwitchStatement(node);
            case "TryStatement":
                return this.evalTryStatement(node);
            case "ReturnStatement":
                throw new ReturnSignal([this.lastResult]);
            case "BreakStatement":
                throw new BreakSignal();
            case "ContinueStatement":
                throw new ContinueSignal();
            case "Block":
                return this.evalBlock(node);
            case "StructAccess":
                return this.evalStructAccess(node);
            case "FunctionHandle":
                return { type: "handle", name: node.name };
            default:
                throw new MatlabRuntimeError(`Unknown node type: ${node.type}`);
        }
    }
    evalIdentifier(node) {
        const value = this.env.get(node.name);
        if (value !== undefined) {
            return value;
        }
        // Check if it's the imaginary unit i or j (when not overwritten)
        if (node.name === "i" || node.name === "j") {
            return { re: 0, im: 1 };
        }
        // Special handling for colon `:` as "all elements" in indexing
        if (node.name === ":") {
            return { type: "colon" };
        }
        // Special handling for `end` keyword in indexing
        if (node.name === "end") {
            return { type: "end" };
        }
        // MATLAB-style command syntax: allow calling these commands without parentheses
        // e.g., `clear` is equivalent to `clear()`
        const commandStyleFunctions = [
            "clear",
            "clearvars",
            "clearVars",
            "who",
            "whoVars",
            "whos",
            "whosVars",
            "clc",
            // Graphics commands and named colormaps, so that `cb = colorbar` and
            // `flipud(parula)` call the function instead of yielding a handle.
            "close",
            "figure",
            "gca",
            "gcf",
            "colorbar",
            "drawnow",
            "hold",
            "parula",
            "jet",
            "gray",
            "hot",
            "cool",
        ];
        if (commandStyleFunctions.includes(node.name)) {
            // Treat as a zero-argument function call
            return this.evalFunctionCall({
                name: node.name,
                args: [],
            });
        }
        // Check if it's a builtin function (for function handles)
        if (matlabBuiltins[node.name]) {
            return { type: "handle", name: node.name };
        }
        throw new MatlabRuntimeError(`Undefined variable or function: ${node.name}`);
    }
    evalBinaryOp(node) {
        const left = this.eval(node.left);
        const right = this.eval(node.right);
        // Matrix/array operations
        if (Array.isArray(left) || Array.isArray(right)) {
            const result = this.matrixOp(node.operator, left, right);
            return PREDICATE_OPERATORS.has(node.operator)
                ? markLogical(result)
                : result;
        }
        // Complex operations
        if (this.isComplex(left) || this.isComplex(right)) {
            return this.complexOp(node.operator, left, right);
        }
        // Scalar operations
        const l = this.toNumber(left);
        const r = this.toNumber(right);
        switch (node.operator) {
            case "+":
                return l + r;
            case "-":
                return l - r;
            case "*":
                return l * r;
            case "/":
                return l / r;
            case "\\":
                return r / l; // Left division
            case "^":
                return Math.pow(l, r);
            case ".*":
                return l * r;
            case "./":
                return l / r;
            case ".^":
                return Math.pow(l, r);
            case "==":
                return l === r ? 1 : 0;
            case "~=":
                return l !== r ? 1 : 0;
            case "<":
                return l < r ? 1 : 0;
            case "<=":
                return l <= r ? 1 : 0;
            case ">":
                return l > r ? 1 : 0;
            case ">=":
                return l >= r ? 1 : 0;
            case "&":
                return l !== 0 && r !== 0 ? 1 : 0;
            case "|":
                return l !== 0 || r !== 0 ? 1 : 0;
            case "&&":
                return l !== 0 && r !== 0 ? 1 : 0;
            case "||":
                return l !== 0 || r !== 0 ? 1 : 0;
            default:
                throw new MatlabRuntimeError(`Unknown operator: ${node.operator}`);
        }
    }
    evalUnaryOp(node) {
        const operand = this.eval(node.operand);
        switch (node.operator) {
            case "-":
                if (Array.isArray(operand)) {
                    return this.mapArray(operand, (v) => this.negate(v));
                }
                if (this.isComplex(operand)) {
                    return { re: -operand.re, im: -operand.im };
                }
                return -this.toNumber(operand);
            case "~":
                if (Array.isArray(operand)) {
                    return markLogical(this.mapArray(operand, (v) => (this.toNumber(v) === 0 ? 1 : 0)));
                }
                return this.toNumber(operand) === 0 ? 1 : 0;
            case "'": // Conjugate transpose
                return this.ctranspose(operand);
            case ".'": // Transpose
                return this.transpose(operand);
            default:
                throw new MatlabRuntimeError(`Unknown unary operator: ${node.operator}`);
        }
    }
    evalFunctionCall(node) {
        const args = node.args.map((arg) => this.eval(arg));
        // Check for user-defined function
        const userFn = this.env.get(node.name);
        if (userFn && typeof userFn === "object" && "type" in userFn) {
            if (userFn.type === "function") {
                return this.callUserFunction(userFn, args);
            }
            if (userFn.type === "anonymous") {
                return this.callAnonymousFunction(userFn, args);
            }
        }
        // Check for classdef constructor
        if (hasClass(node.name)) {
            return constructObject(node.name, args, this.env);
        }
        // Special handling for workspace and file I/O commands BEFORE builtin check
        // These commands need environment access or return async operations
        // Workspace inspection commands
        if (node.name === "who" || node.name === "whoVars") {
            return this.env.getVariableNames();
        }
        if (node.name === "whos" || node.name === "whosVars") {
            const vars = this.env.getAllVariables();
            const result = [];
            for (const [name, value] of Object.entries(vars)) {
                result.push({
                    name,
                    size: getValueSize(value),
                    bytes: estimateBytes(value),
                    class: getValueClass(value),
                });
            }
            return result;
        }
        if (node.name === "clear" ||
            node.name === "clearVars" ||
            node.name === "clearvars") {
            // clear specific variables or all
            const clearsEverything = args.length === 1 &&
                ["all", "-all", "variables", "global"].includes(String(args[0]));
            if (args.length > 0 && !clearsEverything) {
                const varNames = args.map((a) => String(a));
                this.env.clearVariables(varNames);
            }
            else {
                this.env.clearVariables();
            }
            return 0;
        }
        // File I/O commands that return async operation markers
        const ioContext = this.env.getIOContext();
        if (node.name === "save" || node.name === "saveVars") {
            const filename = args.length > 0 ? String(args[0]) : "workspace.mat";
            const varNames = args.slice(1).map((a) => String(a));
            const varsToSave = varNames.length > 0
                ? Object.fromEntries(varNames
                    .map((n) => [n, this.env.get(n)])
                    .filter(([_, v]) => v !== undefined))
                : this.env.getAllVariables();
            // Return async operation marker for frontend to handle
            return {
                type_: "asyncOp",
                operation: "save",
                filename,
                variables: varsToSave,
                variableNames: Object.keys(varsToSave),
                ioContext: ioContext ? "available" : "unavailable",
            };
        }
        if (node.name === "load" || node.name === "loadVars") {
            const filename = args.length > 0 ? String(args[0]) : "";
            const varNames = args.slice(1).map((a) => String(a));
            // Return async operation marker for frontend to handle
            return {
                type_: "asyncOp",
                operation: "load",
                filename,
                variableNames: varNames.length > 0 ? varNames : null,
                ioContext: ioContext ? "available" : "unavailable",
            };
        }
        if (node.name === "csvread") {
            const filename = args.length > 0 ? String(args[0]) : "";
            const rowOffset = args.length > 1 ? Number(args[1]) : 0;
            const colOffset = args.length > 2 ? Number(args[2]) : 0;
            return {
                type_: "asyncOp",
                operation: "csvread",
                filename,
                rowOffset,
                colOffset,
                ioContext: ioContext ? "available" : "unavailable",
            };
        }
        if (node.name === "csvwrite") {
            const filename = args.length > 0 ? String(args[0]) : "";
            const data = args.length > 1 ? args[1] : [];
            const rowOffset = args.length > 2 ? Number(args[2]) : 0;
            const colOffset = args.length > 3 ? Number(args[3]) : 0;
            return {
                type_: "asyncOp",
                operation: "csvwrite",
                filename,
                data,
                rowOffset,
                colOffset,
                ioContext: ioContext ? "available" : "unavailable",
            };
        }
        if (node.name === "dir") {
            const pattern = args.length > 0 ? String(args[0]) : "*";
            return {
                type_: "asyncOp",
                operation: "dir",
                pattern,
                ioContext: ioContext ? "available" : "unavailable",
            };
        }
        if (node.name === "fileread") {
            const filename = args.length > 0 ? String(args[0]) : "";
            return {
                type_: "asyncOp",
                operation: "fileread",
                filename,
                ioContext: ioContext ? "available" : "unavailable",
            };
        }
        if (node.name === "importdata") {
            const filename = args.length > 0 ? String(args[0]) : "";
            return {
                type_: "asyncOp",
                operation: "importdata",
                filename,
                ioContext: ioContext ? "available" : "unavailable",
            };
        }
        // Open a local file: prompts the OS file picker, copies the chosen file
        // into the workspace, and returns its filename for load/fileread/csvread.
        if (node.name === "uigetfile" ||
            node.name === "uiopen" ||
            node.name === "open") {
            const filter = args.length > 0 ? String(args[0]) : "";
            const title = args.length > 1 ? String(args[1]) : "";
            return {
                type_: "asyncOp",
                operation: "uigetfile",
                filter,
                title,
                ioContext: ioContext ? "available" : "unavailable",
            };
        }
        // exist() - check if variable or file exists
        if (node.name === "exist" || node.name === "existVar") {
            const name = args.length > 0 ? String(args[0]) : "";
            const typeArg = args.length > 1 ? String(args[1]).toLowerCase() : "";
            // Check for variable in workspace
            if (typeArg === "" || typeArg === "var") {
                if (this.env.has(name))
                    return 1;
            }
            // Check for builtin
            if (typeArg === "" || typeArg === "builtin") {
                if (matlabBuiltins[name])
                    return 5;
            }
            // For file existence, return async op marker
            if (typeArg === "file" || typeArg === "") {
                return {
                    type_: "asyncOp",
                    operation: "exist",
                    name,
                    checkType: typeArg || "any",
                    ioContext: ioContext ? "available" : "unavailable",
                };
            }
            return 0;
        }
        // Check for builtin function
        const builtin = matlabBuiltins[node.name];
        if (builtin) {
            // Special handling: loadToolbox evaluates the toolbox code in the current env
            if (node.name === "loadToolbox" && typeof args[0] === "string") {
                const tb = getToolbox(args[0]);
                if (tb && tb.code) {
                    const tokens = new MatlabLexer(tb.code).tokenize();
                    const ast = new MatlabParser(tokens).parse();
                    const evaluator = new MatlabEvaluator(this.env);
                    evaluator.evaluate(ast);
                }
                return builtin(...args);
            }
            return builtin(...args);
        }
        // Could be array indexing
        if (userFn && Array.isArray(userFn)) {
            return this.indexArray(userFn, args);
        }
        throw new MatlabRuntimeError(`Undefined function: ${node.name}`);
    }
    evalArrayLiteral(node) {
        const rows = node.rows.map((row) => row.map((elem) => this.eval(elem)));
        // A bracketed row of char literals is character concatenation, which is
        // how a long format string is split across continued lines:
        //   ['first part ' ...
        //    'second part']
        // Double quotes make string objects instead, and ["a", "b"] stays a
        // two-element array.
        const isCharLiteral = (elem) => elem.type === "StringLiteral" &&
            elem.quote !== '"';
        if (node.rows.length === 1 &&
            node.rows[0].length > 1 &&
            node.rows[0].every(isCharLiteral)) {
            return rows[0].join("");
        }
        // If single row, return as 1D array
        if (rows.length === 1) {
            return rows[0];
        }
        // Multiple rows = matrix
        return rows;
    }
    evalCellArray(node) {
        const rows = node.rows.map((row) => row.map((elem) => this.eval(elem)));
        return rows;
    }
    evalIndexAccess(node) {
        const obj = this.eval(node.object);
        const indices = node.indices.map((idx) => this.eval(idx));
        return this.indexArray(obj, indices);
    }
    evalRangeExpr(node) {
        const start = this.toNumber(this.eval(node.start));
        const end = this.toNumber(this.eval(node.end));
        const step = node.step ? this.toNumber(this.eval(node.step)) : 1;
        if (step === 0) {
            throw new MatlabRuntimeError("Range step cannot be zero");
        }
        const result = [];
        if (step > 0) {
            for (let i = start; i <= end + 1e-10; i += step) {
                result.push(i);
            }
        }
        else {
            for (let i = start; i >= end - 1e-10; i += step) {
                result.push(i);
            }
        }
        return result;
    }
    evalAssignment(node) {
        const value = this.eval(node.value);
        if (node.target.type === "Identifier") {
            // File I/O returns an async marker resolved by the frontend. Record the
            // target name on the marker so the resolved value is bound to it there,
            // instead of storing the (unresolved) marker in the variable.
            if (isAsyncOperation(value)) {
                value.assignTo =
                    node.target.name;
                this.lastResult = value;
                return value;
            }
            this.env.set(node.target.name, value);
            this.lastResult = value;
            return value;
        }
        if (node.target.type === "IndexAccess") {
            const objName = node.target.object.name;
            const obj = this.env.get(objName) || [];
            const indices = node.target.indices.map((idx) => this.eval(idx));
            const updated = this.setIndex(obj, indices, value);
            this.env.set(objName, updated);
            this.lastResult = value;
            return value;
        }
        if (node.target.type === "StructAccess") {
            const obj = this.eval(node.target.object);
            obj[node.target.field] = value;
            this.lastResult = value;
            return value;
        }
        throw new MatlabRuntimeError("Invalid assignment target");
    }
    evalMultiAssignment(node) {
        const values = this.eval(node.value);
        // Handle plain objects (e.g. {X, Y} from meshgrid) by extracting values in order
        if (values !== null &&
            typeof values === "object" &&
            !Array.isArray(values) &&
            !("re" in values) &&
            !("type" in values)) {
            const objValues = Object.values(values);
            for (let i = 0; i < node.targets.length && i < objValues.length; i++) {
                const target = node.targets[i];
                if (target.type === "Identifier" && target.name !== "~") {
                    this.env.set(target.name, objValues[i]);
                }
            }
            return objValues[0];
        }
        if (!Array.isArray(values)) {
            // Single value, assign to first target
            const target = node.targets[0];
            if (node.targets.length > 0 &&
                target.type === "Identifier" &&
                target.name !== "~") {
                this.env.set(target.name, values);
            }
            return values;
        }
        // Assign each value to corresponding target
        for (let i = 0; i < node.targets.length && i < values.length; i++) {
            const target = node.targets[i];
            if (target.type === "Identifier" && target.name !== "~") {
                this.env.set(target.name, values[i]);
            }
        }
        return values;
    }
    evalFunctionDeclaration(node) {
        const fn = {
            type: "function",
            params: node.params,
            outputs: node.outputs,
            body: node.body,
            closure: this.env,
        };
        this.env.set(node.name, fn);
        return fn;
    }
    evalAnonymousFunction(node) {
        return {
            type: "anonymous",
            params: node.params,
            body: node.body,
            closure: this.env,
        };
    }
    evalIfStatement(node) {
        if (this.isTruthy(this.eval(node.condition))) {
            return this.eval(node.thenBranch);
        }
        if (node.elseifBranches) {
            for (const branch of node.elseifBranches) {
                if (this.isTruthy(this.eval(branch.condition))) {
                    return this.eval(branch.body);
                }
            }
        }
        if (node.elseBranch) {
            return this.eval(node.elseBranch);
        }
        return null;
    }
    evalForLoop(node) {
        const range = this.eval(node.range);
        let result = null;
        if (Array.isArray(range)) {
            // Iterate over columns for matrix, elements for array
            const values = this.isMatrix(range) ? this.getColumns(range) : range;
            for (const value of values) {
                this.env.set(node.variable, value);
                try {
                    result = this.eval(node.body);
                }
                catch (e) {
                    if (e instanceof BreakSignal)
                        break;
                    if (e instanceof ContinueSignal)
                        continue;
                    throw e;
                }
            }
        }
        return result;
    }
    evalWhileLoop(node) {
        let result = null;
        let iterations = 0;
        const maxIterations = 100000;
        while (this.isTruthy(this.eval(node.condition))) {
            if (++iterations > maxIterations) {
                throw new MatlabRuntimeError("Maximum iterations exceeded");
            }
            try {
                result = this.eval(node.body);
            }
            catch (e) {
                if (e instanceof BreakSignal)
                    break;
                if (e instanceof ContinueSignal)
                    continue;
                throw e;
            }
        }
        return result;
    }
    evalSwitchStatement(node) {
        const expr = this.eval(node.expression);
        for (const c of node.cases) {
            for (const v of c.values) {
                if (this.isEqual(expr, this.eval(v))) {
                    return this.eval(c.body);
                }
            }
        }
        if (node.otherwise) {
            return this.eval(node.otherwise);
        }
        return null;
    }
    evalTryStatement(node) {
        try {
            return this.eval(node.tryBlock);
        }
        catch (e) {
            if (node.catchBlock) {
                if (node.catchVar && e instanceof Error) {
                    this.env.set(node.catchVar, e.message);
                }
                return this.eval(node.catchBlock);
            }
            return null;
        }
    }
    evalBlock(node) {
        let result = null;
        hoistFunctionDeclarations(node.statements, this);
        for (const stmt of node.statements) {
            result = this.eval(stmt);
            this.lastResult = result;
        }
        return result;
    }
    evalStructAccess(node) {
        const obj = this.eval(node.object);
        // Handle classdef objects
        if (typeof obj === "object" &&
            obj !== null &&
            "type" in obj &&
            obj.type === "object") {
            return getProperty(obj, node.field, this.env);
        }
        const struct = obj;
        if (typeof struct !== "object" ||
            struct === null ||
            !(node.field in struct)) {
            throw new MatlabRuntimeError(`Unknown field: ${node.field}`);
        }
        return struct[node.field];
    }
    // Helper methods
    isComplex(v) {
        return typeof v === "object" && v !== null && "re" in v && "im" in v;
    }
    isMatrix(v) {
        return Array.isArray(v) && v.length > 0 && Array.isArray(v[0]);
    }
    toNumber(v) {
        if (typeof v === "number")
            return v;
        if (this.isComplex(v))
            return Math.sqrt(v.re * v.re + v.im * v.im);
        if (Array.isArray(v) && v.length === 1) {
            if (Array.isArray(v[0]) && v[0].length === 1) {
                return this.toNumber(v[0][0]);
            }
            return this.toNumber(v[0]);
        }
        throw new MatlabRuntimeError("Cannot convert to number");
    }
    isTruthy(v) {
        if (typeof v === "number")
            return v !== 0;
        if (this.isComplex(v))
            return v.re !== 0 || v.im !== 0;
        if (Array.isArray(v)) {
            const flat = v.flat(Infinity);
            return flat.every((x) => this.isTruthy(x));
        }
        return v !== null;
    }
    isEqual(a, b) {
        if (typeof a === "number" && typeof b === "number")
            return a === b;
        if (typeof a === "string" && typeof b === "string")
            return a === b;
        return JSON.stringify(a) === JSON.stringify(b);
    }
    negate(v) {
        if (typeof v === "number")
            return -v;
        if (this.isComplex(v))
            return { re: -v.re, im: -v.im };
        if (Array.isArray(v))
            return this.mapArray(v, (x) => this.negate(x));
        throw new MatlabRuntimeError("Cannot negate value");
    }
    mapArray(arr, fn) {
        if (!Array.isArray(arr))
            return fn(arr);
        return arr.map((v) => Array.isArray(v) ? this.mapArray(v, fn) : fn(v));
    }
    transpose(v) {
        if (!Array.isArray(v))
            return v;
        if (!this.isMatrix(v)) {
            // Convert vector to column
            return v.map((x) => [x]);
        }
        const rows = v.length;
        const cols = v[0].length;
        const result = [];
        for (let j = 0; j < cols; j++) {
            const row = [];
            for (let i = 0; i < rows; i++) {
                row.push(v[i][j]);
            }
            result.push(row);
        }
        return result;
    }
    ctranspose(v) {
        const t = this.transpose(v);
        return this.mapArray(t, (x) => {
            if (this.isComplex(x))
                return { re: x.re, im: -x.im };
            return x;
        });
    }
    matrixOp(op, left, right) {
        const isElementWise = op.startsWith(".");
        const baseOp = isElementWise ? op.slice(1) : op;
        const leftIsScalar = !Array.isArray(left);
        const rightIsScalar = !Array.isArray(right);
        // If either operand is scalar, use element-wise operation
        if (leftIsScalar || rightIsScalar) {
            return this.elementWiseOp(baseOp, left, right);
        }
        // Element-wise operations
        if (isElementWise || this.isScalarOp(op)) {
            return this.elementWiseOp(baseOp, left, right);
        }
        // Matrix multiplication
        if (op === "*") {
            return this.matrixMultiply(left, right);
        }
        // Matrix division
        if (op === "/") {
            // A / B = A * inv(B)
            const invB = matlabBuiltins.inv(right);
            return this.matrixMultiply(left, invB);
        }
        if (op === "\\") {
            // A \ B = inv(A) * B
            const invA = matlabBuiltins.inv(left);
            return this.matrixMultiply(invA, right);
        }
        // Matrix power
        if (op === "^") {
            return this.matrixPower(left, right);
        }
        return this.elementWiseOp(op, left, right);
    }
    isScalarOp(op) {
        return [
            "+",
            "-",
            "==",
            "~=",
            "<",
            "<=",
            ">",
            ">=",
            "&",
            "|",
            "&&",
            "||",
        ].includes(op);
    }
    elementWiseOp(op, left, right) {
        const leftIsArray = Array.isArray(left);
        const rightIsArray = Array.isArray(right);
        // An empty operand gives an empty result, as in MATLAB. This comes up
        // whenever a logical mask selects nothing: d(mask)./abs(d(mask)).
        const isEmpty = (v) => Array.isArray(v) && v.flat().length === 0;
        if (isEmpty(left) || isEmpty(right))
            return [];
        if (!leftIsArray && !rightIsArray) {
            return this.scalarOp(op, left, right);
        }
        if (!leftIsArray) {
            // Scalar with array
            return this.mapArray(right, (v) => this.scalarOp(op, left, v));
        }
        if (!rightIsArray) {
            // Array with scalar
            return this.mapArray(left, (v) => this.scalarOp(op, v, right));
        }
        // Check if either input is a 1D array (row vector)
        const leftIs1D = !this.isMatrix(left);
        const rightIs1D = !this.isMatrix(right);
        // Both arrays - element-wise
        const leftFlat = this.isMatrix(left) ? left : [left];
        const rightFlat = this.isMatrix(right) ? right : [right];
        const rows = Math.max(leftFlat.length, rightFlat.length);
        const cols = Math.max(leftFlat[0]?.length || 1, rightFlat[0]?.length || 1);
        const result = [];
        for (let i = 0; i < rows; i++) {
            const row = [];
            for (let j = 0; j < cols; j++) {
                const l = leftFlat[i % leftFlat.length]?.[j % (leftFlat[0]?.length || 1)] ??
                    left;
                const r = rightFlat[i % rightFlat.length]?.[j % (rightFlat[0]?.length || 1)] ??
                    right;
                row.push(this.scalarOp(op, l, r));
            }
            result.push(row);
        }
        // Simplify result based on input shapes
        if (result.length === 1 && result[0].length === 1) {
            return result[0][0];
        }
        // If both inputs were 1D arrays, return 1D array
        if (leftIs1D && rightIs1D && result.length === 1) {
            return result[0];
        }
        return result;
    }
    scalarOp(op, left, right) {
        if (this.isComplex(left) || this.isComplex(right)) {
            return this.complexOp(op, left, right);
        }
        const l = this.toNumber(left);
        const r = this.toNumber(right);
        switch (op) {
            case "+":
                return l + r;
            case "-":
                return l - r;
            case "*":
                return l * r;
            case "/":
                return l / r;
            case "\\":
                return r / l;
            case "^":
                return Math.pow(l, r);
            case "==":
                return l === r ? 1 : 0;
            case "~=":
                return l !== r ? 1 : 0;
            case "<":
                return l < r ? 1 : 0;
            case "<=":
                return l <= r ? 1 : 0;
            case ">":
                return l > r ? 1 : 0;
            case ">=":
                return l >= r ? 1 : 0;
            case "&":
                return l !== 0 && r !== 0 ? 1 : 0;
            case "|":
                return l !== 0 || r !== 0 ? 1 : 0;
            default:
                throw new MatlabRuntimeError(`Unknown operator: ${op}`);
        }
    }
    complexOp(op, left, right) {
        const l = this.toComplex(left);
        const r = this.toComplex(right);
        switch (op) {
            case "+":
                return { re: l.re + r.re, im: l.im + r.im };
            case "-":
                return { re: l.re - r.re, im: l.im - r.im };
            case "*":
                return {
                    re: l.re * r.re - l.im * r.im,
                    im: l.re * r.im + l.im * r.re,
                };
            case "/": {
                const denom = r.re * r.re + r.im * r.im;
                return {
                    re: (l.re * r.re + l.im * r.im) / denom,
                    im: (l.im * r.re - l.re * r.im) / denom,
                };
            }
            case "^": {
                // Complex power using polar form
                const mag = Math.sqrt(l.re * l.re + l.im * l.im);
                const arg = Math.atan2(l.im, l.re);
                const newMag = Math.pow(mag, r.re) * Math.exp(-arg * r.im);
                const newArg = r.re * arg + r.im * Math.log(mag);
                return {
                    re: newMag * Math.cos(newArg),
                    im: newMag * Math.sin(newArg),
                };
            }
            case "==":
                return l.re === r.re && l.im === r.im ? 1 : 0;
            case "~=":
                return l.re !== r.re || l.im !== r.im ? 1 : 0;
            default:
                throw new MatlabRuntimeError(`Operator ${op} not supported for complex numbers`);
        }
    }
    toComplex(v) {
        if (this.isComplex(v))
            return v;
        if (typeof v === "number")
            return { re: v, im: 0 };
        throw new MatlabRuntimeError("Cannot convert to complex");
    }
    matrixMultiply(left, right) {
        const A = this.toMatrix(left);
        const B = this.toMatrix(right);
        const m = A.length;
        const n = A[0].length;
        const p = B[0].length;
        if (n !== B.length) {
            throw new MatlabRuntimeError("Matrix dimensions must agree for multiplication");
        }
        const result = [];
        for (let i = 0; i < m; i++) {
            const row = [];
            for (let j = 0; j < p; j++) {
                let sum = 0;
                for (let k = 0; k < n; k++) {
                    const product = this.scalarOp("*", A[i][k], B[k][j]);
                    sum = this.scalarOp("+", sum, product);
                }
                row.push(sum);
            }
            result.push(row);
        }
        return result;
    }
    matrixPower(base, exp) {
        const n = this.toNumber(exp);
        if (n === 0) {
            const size = this.isMatrix(base) ? base.length : 1;
            return matlabBuiltins.eye(size);
        }
        if (n === 1)
            return base;
        if (n < 0) {
            const inv = matlabBuiltins.inv(base);
            return this.matrixPower(inv, -n);
        }
        let result = base;
        for (let i = 1; i < n; i++) {
            result = this.matrixMultiply(result, base);
        }
        return result;
    }
    toMatrix(v) {
        if (this.isMatrix(v))
            return v;
        if (Array.isArray(v))
            return [v];
        return [[v]];
    }
    getColumns(m) {
        const cols = [];
        for (let j = 0; j < m[0].length; j++) {
            const col = [];
            for (let i = 0; i < m.length; i++) {
                col.push(m[i][j]);
            }
            cols.push(col);
        }
        return cols;
    }
    indexArray(arr, indices) {
        if (!Array.isArray(arr)) {
            if (indices.length === 1 && this.toNumber(indices[0]) === 1) {
                return arr;
            }
            throw new MatlabRuntimeError("Indexing error");
        }
        if (indices.length === 1) {
            let idx = indices[0];
            // Handle 'end' by replacing with last index
            if (typeof idx === "object" &&
                idx !== null &&
                "type" in idx &&
                idx.type === "end") {
                idx = arr.flat().length;
            }
            // Colon returns everything
            if ((typeof idx === "string" && idx === ":") ||
                (typeof idx === "object" &&
                    idx !== null &&
                    "type" in idx &&
                    idx.type === "colon")) {
                return arr.flat();
            }
            // Logical indexing: select the elements the mask marks, in the same
            // order the array itself flattens.
            if (isLogicalMask(idx)) {
                const flat = arr.flat();
                const mask = idx.flat();
                const selected = [];
                for (let i = 0; i < mask.length; i++) {
                    if (this.toNumber(mask[i]) !== 0)
                        selected.push(flat[i]);
                }
                return selected;
            }
            // Array indexing
            if (Array.isArray(idx)) {
                return idx.map((i) => {
                    const index = this.toNumber(i) - 1;
                    const flat = arr.flat();
                    return flat[index];
                });
            }
            // Linear indexing
            const index = this.toNumber(idx) - 1;
            const flat = arr.flat();
            return flat[index];
        }
        // 2D indexing
        if (indices.length === 2) {
            let [rowIdx, colIdx] = indices;
            const matrix = this.isMatrix(arr) ? arr : [arr];
            // Helper to check if value is a colon identifier
            const isColonId = (v) => v === ":" ||
                (typeof v === "object" &&
                    v !== null &&
                    "type" in v &&
                    v.type === "colon") ||
                (typeof v === "object" &&
                    v !== null &&
                    "name" in v &&
                    v.name === ":");
            // Helper to check if value is an 'end' identifier
            const isEndId = (v) => typeof v === "object" &&
                v !== null &&
                "type" in v &&
                v.type === "end";
            // Replace 'end' with actual last index
            if (isEndId(rowIdx)) {
                rowIdx = matrix.length;
            }
            if (isEndId(colIdx)) {
                colIdx = matrix[0].length;
            }
            // Handle colon for rows
            const rows = isColonId(rowIdx)
                ? matrix.map((_, i) => i)
                : Array.isArray(rowIdx)
                    ? rowIdx.map((i) => this.toNumber(i) - 1)
                    : [this.toNumber(rowIdx) - 1];
            // Handle colon for columns
            const cols = isColonId(colIdx)
                ? matrix[0].map((_, i) => i)
                : Array.isArray(colIdx)
                    ? colIdx.map((i) => this.toNumber(i) - 1)
                    : [this.toNumber(colIdx) - 1];
            // Extract submatrix
            const result = [];
            for (const r of rows) {
                const row = [];
                for (const c of cols) {
                    row.push(matrix[r][c]);
                }
                result.push(row);
            }
            // Simplify if single element
            if (result.length === 1 && result[0].length === 1) {
                return result[0][0];
            }
            // Simplify if single row (return as 1D array)
            if (result.length === 1) {
                return result[0];
            }
            return result;
        }
        throw new MatlabRuntimeError("Invalid indexing");
    }
    setIndex(arr, indices, value) {
        if (!Array.isArray(arr)) {
            arr = [[arr]];
        }
        const wasRowVector = !this.isMatrix(arr);
        const matrix = this.isMatrix(arr)
            ? [...arr.map((r) => [...r])]
            : [[...arr]];
        // Helper: resolve an index value to an array of 0-based indices
        const resolveIndex = (idx, dimSize) => {
            if (typeof idx === "string" && idx === ":") {
                // colon means all indices
                return Array.from({ length: dimSize }, (_, i) => i);
            }
            if (Array.isArray(idx)) {
                // Range or array of indices — flatten and convert to 0-based
                const flat = [];
                for (const el of idx) {
                    if (Array.isArray(el)) {
                        for (const e of el) {
                            flat.push(this.toNumber(e) - 1);
                        }
                    }
                    else {
                        flat.push(this.toNumber(el) - 1);
                    }
                }
                return flat;
            }
            return [this.toNumber(idx) - 1];
        };
        if (indices.length === 1 && isLogicalMask(indices[0])) {
            // arr(mask) = value. A scalar value fills every marked element; an
            // array value is consumed one element per marked position.
            const mask = indices[0].flat();
            const replacements = Array.isArray(value)
                ? value.flat()
                : null;
            let pos = 0;
            let taken = 0;
            for (let r = 0; r < matrix.length; r++) {
                for (let c = 0; c < matrix[r].length; c++, pos++) {
                    if (this.toNumber(mask[pos] ?? 0) === 0)
                        continue;
                    matrix[r][c] = replacements ? (replacements[taken++] ?? 0) : value;
                }
            }
            // Keep a row vector a row vector.
            return wasRowVector && matrix.length === 1 ? matrix[0] : matrix;
        }
        if (indices.length === 1) {
            const idxArr = resolveIndex(indices[0], matrix.length * (matrix[0]?.length || 1));
            if (idxArr.length === 1) {
                const index = idxArr[0];
                // For column vectors (nx1 matrices), use row indexing
                if (matrix[0].length === 1) {
                    while (matrix.length <= index) {
                        matrix.push([0]);
                    }
                    matrix[index][0] = value;
                    return matrix;
                }
                // For row vectors (1xn), use column indexing
                if (matrix.length === 1) {
                    while (matrix[0].length <= index) {
                        matrix[0].push(0);
                    }
                    matrix[0][index] = value;
                    return matrix;
                }
                // For general matrices, use column-major linear indexing
                const rows = matrix.length;
                const row = index % rows;
                const col = Math.floor(index / rows);
                for (const r of matrix) {
                    while (r.length <= col) {
                        r.push(0);
                    }
                }
                matrix[row][col] = value;
                return matrix;
            }
            // Multiple indices — assign value to each
            for (const index of idxArr) {
                if (matrix.length === 1) {
                    while (matrix[0].length <= index)
                        matrix[0].push(0);
                    matrix[0][index] = typeof value === "number" ? value : value;
                }
                else if (matrix[0].length === 1) {
                    while (matrix.length <= index)
                        matrix.push([0]);
                    matrix[index][0] = typeof value === "number" ? value : value;
                }
                else {
                    const rows = matrix.length;
                    const row = index % rows;
                    const col = Math.floor(index / rows);
                    for (const r of matrix) {
                        while (r.length <= col)
                            r.push(0);
                    }
                    matrix[row][col] = typeof value === "number" ? value : value;
                }
            }
            return matrix;
        }
        if (indices.length === 2) {
            const rowIndices = resolveIndex(indices[0], matrix.length);
            const colIndices = resolveIndex(indices[1], matrix[0]?.length || 0);
            // Ensure matrix is large enough
            const maxRow = Math.max(...rowIndices);
            const maxCol = Math.max(...colIndices);
            while (matrix.length <= maxRow) {
                matrix.push(Array(matrix[0]?.length || 0).fill(0));
            }
            for (const r of matrix) {
                while (r.length <= maxCol) {
                    r.push(0);
                }
            }
            // Check if value is a matrix to assign element-by-element
            const valIsMatrix = Array.isArray(value) && Array.isArray(value[0]);
            const valIsArray = Array.isArray(value) && !valIsMatrix;
            for (let ri = 0; ri < rowIndices.length; ri++) {
                for (let ci = 0; ci < colIndices.length; ci++) {
                    const row = rowIndices[ri];
                    const col = colIndices[ci];
                    if (valIsMatrix) {
                        matrix[row][col] = value[ri]?.[ci] ?? 0;
                    }
                    else if (valIsArray) {
                        matrix[row][col] =
                            value[ri * colIndices.length + ci] ?? 0;
                    }
                    else {
                        matrix[row][col] = value;
                    }
                }
            }
            return matrix;
        }
        throw new MatlabRuntimeError("Invalid indexing");
    }
    callUserFunction(fn, args) {
        const childEnv = fn.closure.createChild();
        // Set nargin/nargout
        childEnv.set("nargin", args.length);
        childEnv.set("nargout", fn.outputs.length || 1);
        // Bind parameters, with varargin support
        const hasVarargin = fn.params.length > 0 && fn.params[fn.params.length - 1] === "varargin";
        const fixedParams = hasVarargin ? fn.params.slice(0, -1) : fn.params;
        for (let i = 0; i < fixedParams.length; i++) {
            childEnv.set(fixedParams[i], args[i] ?? 0);
        }
        if (hasVarargin) {
            // Collect remaining args into a cell array (array of arrays)
            const vararginValues = args.slice(fixedParams.length);
            childEnv.set("varargin", vararginValues);
        }
        // Initialize outputs
        const hasVarargout = fn.outputs.length > 0 &&
            fn.outputs[fn.outputs.length - 1] === "varargout";
        const fixedOutputs = hasVarargout ? fn.outputs.slice(0, -1) : fn.outputs;
        for (const out of fixedOutputs) {
            childEnv.set(out, 0);
        }
        if (hasVarargout) {
            childEnv.set("varargout", []);
        }
        // Execute
        const evaluator = new MatlabEvaluator(childEnv);
        try {
            evaluator.evaluate(fn.body);
        }
        catch (e) {
            if (e instanceof ReturnSignal) {
                // Return caught
            }
            else {
                throw e;
            }
        }
        // Get output values
        const outputs = fixedOutputs.map((name) => childEnv.get(name) ?? 0);
        // Append varargout values if present
        if (hasVarargout) {
            const varargoutVal = childEnv.get("varargout");
            if (Array.isArray(varargoutVal)) {
                outputs.push(...varargoutVal);
            }
        }
        return outputs.length === 1 ? outputs[0] : outputs;
    }
    callAnonymousFunction(fn, args) {
        const childEnv = fn.closure.createChild();
        // Bind parameters
        for (let i = 0; i < fn.params.length; i++) {
            childEnv.set(fn.params[i], args[i] ?? 0);
        }
        const evaluator = new MatlabEvaluator(childEnv);
        return evaluator.evaluate(fn.body);
    }
}
/**
 * Helper function to get the size string of a value (for whos command)
 */
function getValueSize(value) {
    if (value === null || value === undefined)
        return "0x0";
    if (typeof value === "number")
        return "1x1";
    if (typeof value === "string")
        return `1x${value.length}`;
    if (typeof value === "object" && "re" in value && "im" in value)
        return "1x1";
    if (Array.isArray(value)) {
        if (value.length === 0)
            return "0x0";
        if (Array.isArray(value[0])) {
            // Matrix
            const rows = value.length;
            const cols = Math.max(...value.map((r) => (Array.isArray(r) ? r.length : 1)));
            return `${rows}x${cols}`;
        }
        // 1D array
        return `1x${value.length}`;
    }
    if (typeof value === "object") {
        const keys = Object.keys(value);
        return `1x1 struct (${keys.length} fields)`;
    }
    return "1x1";
}
/**
 * Estimate bytes used by a value (for whos command)
 */
function estimateBytes(value) {
    if (value === null || value === undefined)
        return 0;
    if (typeof value === "number")
        return 8;
    if (typeof value === "string")
        return value.length * 2;
    if (typeof value === "object" && "re" in value && "im" in value)
        return 16;
    if (Array.isArray(value)) {
        // Use explicit cast to avoid TypeScript deep recursion
        return value.flat(Infinity).length * 8;
    }
    if (typeof value === "object") {
        return JSON.stringify(value).length;
    }
    return 8;
}
/**
 * Get the class/type of a value (for whos command)
 */
function getValueClass(value) {
    if (value === null || value === undefined)
        return "null";
    if (typeof value === "number")
        return "double";
    if (typeof value === "string")
        return "char";
    if (typeof value === "object" && "re" in value && "im" in value)
        return "complex";
    if (Array.isArray(value)) {
        if (value.length === 0)
            return "double";
        const first = Array.isArray(value[0]) ? value[0][0] : value[0];
        if (typeof first === "number")
            return "double";
        if (typeof first === "string")
            return "cell";
        return "cell";
    }
    if (typeof value === "object") {
        if ("type" in value) {
            const t = value.type;
            if (t === "function" || t === "anonymous" || t === "handle")
                return "function_handle";
            if (t === "plot")
                return "figure";
        }
        return "struct";
    }
    return "unknown";
}
/**
 * Register every function declared in a list of statements before any of them
 * run. MATLAB scripts conventionally put their local functions at the bottom
 * of the file and call them from the top.
 */
export function hoistFunctionDeclarations(statements, target) {
    const env = target instanceof MatlabEvaluator ? target.getEnvironment() : target;
    for (const stmt of statements) {
        if (stmt.type !== "FunctionDeclaration")
            continue;
        const decl = stmt;
        if (env.get(decl.name) !== undefined)
            continue;
        env.set(decl.name, {
            type: "function",
            params: decl.params,
            outputs: decl.outputs,
            body: decl.body,
            closure: env,
        });
    }
}
/**
 * Convenience function to evaluate MATLAB code
 */
export function matlabEvaluate(node, env) {
    const evaluator = new MatlabEvaluator(env);
    return evaluator.evaluate(node);
}
//# sourceMappingURL=evaluator.js.map